import { useTranslation } from 'react-i18next'

export type ConclusionValue = 'true' | 'false' | 'fallacious' | 'completed'

// ── 12-spike sunburst polygon ─────────────────────────────────────────────────
// viewBox 0 0 100 100, outer radius R=50, inner radius r=43,
// starting at 12 o'clock, alternating outer/inner every 15°.
// r=43 (was 38) gives shallower spikes and more internal space for text.
const STAMP_POINTS = [
  '50,0',
  '61.13,8.47',
  '75,6.70',
  '80.40,19.60',
  '93.30,25',
  '91.53,38.87',
  '100,50',
  '91.53,61.13',
  '93.30,75',
  '80.40,80.40',
  '75,93.30',
  '61.13,91.53',
  '50,100',
  '38.87,91.53',
  '25,93.30',
  '19.60,80.40',
  '6.70,75',
  '8.47,61.13',
  '0,50',
  '8.47,38.87',
  '6.70,25',
  '19.60,19.60',
  '25,6.70',
  '38.87,8.47',
].join(' ')

// ── Colour palettes ───────────────────────────────────────────────────────────

type Palette = { fill: string; stroke: string; textClass: string }

// Token-driven palettes: fill/stroke are SVG attribute values (not
// classNames), so they resolve theme tokens via `hsl(var(--token))` /
// `hsl(var(--token) / alpha)` literal strings — this is valid CSS and works
// fine as an inline SVG attribute value. textClass stays a Tailwind class.
const LIGHT: Record<ConclusionValue, Palette> = {
  true: {
    fill: 'hsl(var(--success) / 0.18)',
    stroke: 'hsl(var(--success))',
    textClass: 'text-success',
  },
  false: {
    fill: 'hsl(var(--destructive) / 0.18)',
    stroke: 'hsl(var(--destructive))',
    textClass: 'text-destructive',
  },
  fallacious: {
    fill: 'hsl(var(--warning) / 0.18)',
    stroke: 'hsl(var(--warning))',
    textClass: 'text-warning',
  },
  completed: {
    fill: 'hsl(var(--info) / 0.18)',
    stroke: 'hsl(var(--info))',
    textClass: 'text-info',
  },
}

const DARK: Record<ConclusionValue, Palette> = {
  true: {
    fill: 'hsl(var(--success) / 0.28)',
    stroke: 'hsl(var(--success))',
    textClass: 'text-success',
  },
  false: {
    fill: 'hsl(var(--destructive) / 0.28)',
    stroke: 'hsl(var(--destructive))',
    textClass: 'text-destructive',
  },
  fallacious: {
    fill: 'hsl(var(--warning) / 0.28)',
    stroke: 'hsl(var(--warning))',
    textClass: 'text-warning',
  },
  completed: {
    fill: 'hsl(var(--info) / 0.28)',
    stroke: 'hsl(var(--info))',
    textClass: 'text-info',
  },
}

// Renders on a solid opaque fill, so text uses the matching `-foreground`
// token per conclusion (rather than a blanket text-white) so a theme with a
// non-white *-foreground still contrasts correctly.
const OPAQUE: Record<ConclusionValue, Palette> = {
  true: {
    fill: 'hsl(var(--success))',
    stroke: '#ffffff',
    textClass: 'text-success-foreground',
  },
  false: {
    fill: 'hsl(var(--destructive))',
    stroke: '#ffffff',
    textClass: 'text-destructive-foreground',
  },
  fallacious: {
    fill: 'hsl(var(--warning))',
    stroke: '#ffffff',
    textClass: 'text-warning-foreground',
  },
  completed: {
    fill: 'hsl(var(--info))',
    stroke: '#ffffff',
    textClass: 'text-info-foreground',
  },
}

// ── Component ─────────────────────────────────────────────────────────────────

interface ConclusionStampProps {
  conclusion: ConclusionValue
  size?: number
  dark?: boolean
  /** Replace the SVG fill with pure black — useful on image/video backgrounds. */
  opaqueBackground?: boolean
  className?: string
}

/**
 * Certification-stamp badge with a 12-spike sunburst SVG border.
 * The outer div accepts any positioning classes (e.g. `absolute bottom-4 right-4`).
 * An inner `relative` div keeps the SVG + content stack correct.
 */
export function ConclusionStamp({
  conclusion,
  size = 72,
  dark = false,
  opaqueBackground = false,
  className = '',
}: ConclusionStampProps) {
  const { t } = useTranslation()

  if (conclusion === 'completed') {
    return null
  }

  const palette = opaqueBackground
    ? OPAQUE[conclusion]
    : dark
      ? DARK[conclusion]
      : LIGHT[conclusion]
  const fontSize = Math.round(size * 0.115)

  const label =
    conclusion === 'true'
      ? t('admin.inv_conclusion_true')
      : conclusion === 'false'
        ? t('admin.inv_conclusion_false')
        : conclusion === 'fallacious'
          ? t('admin.inv_conclusion_fallacious')
          : t('admin.inv_conclusion_completed')

  const glyph =
    conclusion === 'true'
      ? '✓'
      : conclusion === 'false'
        ? '✗'
        : conclusion === 'fallacious'
          ? '!'
          : '−'

  return (
    // Outer div: only sizing + whatever positioning the caller needs.
    // Intentionally NO `relative` here so `absolute` from className is not overridden.
    <div style={{ width: size, height: size }} className={`shrink-0 drop-shadow-xl ${className}`}>
      {/* Inner div: always relative so the SVG can use absolute inset-0 */}
      <div className="relative w-full h-full flex items-center justify-center">
        <svg
          width={size}
          height={size}
          viewBox="0 0 100 100"
          className="absolute inset-0"
          aria-hidden="true"
        >
          <polygon
            points={STAMP_POINTS}
            fill={palette.fill}
            stroke={palette.stroke}
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
        </svg>

        <div
          className={`relative z-10 flex flex-col items-center justify-center gap-0.5 ${palette.textClass}`}
        >
          <span
            style={{ fontSize: `${Math.round(fontSize * 2)}px`, lineHeight: 1 }}
            className="font-black"
          >
            {glyph}
          </span>
          <span
            style={{ fontSize: `${fontSize}px`, lineHeight: 1.1 }}
            className="font-bold text-center px-1"
          >
            {label}
          </span>
        </div>
      </div>
    </div>
  )
}
